import { inject, Injectable, Signal, signal } from "@angular/core";
import { CRUDViewService, emptyPageViewModel, PageViewModel, ViewService } from "../../01_core/state-management/view-service.interface";
import { CustomerModel } from "../../03_model/customer.model";
import { ViewDataCache } from "../../01_core/state-management/view-data-cache";
import { CustomerRepository } from "../../04_repository/customer.repository";
import { Refresher } from "../../01_core/state-management/refresher";
import { PaginationData, paginationData } from "../../02_ui/paginated-list-simple/paginated-list-simple.component";
import { firstValueFrom } from "rxjs";
import { PageQueryConfig } from "../../03_model/repository.model";

const pageSize = 20;
const viewID = 'customerList';

export type CustomerViewModel = PageViewModel<CustomerModel>;
export type CustomerListUIState = {
  displayMode: 'list' | 'cards';
}

export const DEFAULT_QUERY_PARAMETERS: PageQueryConfig = {
  searchParameters: [],
  sortKey: 'createdAt',
  sortDirection: 'desc'
};


@Injectable({ providedIn: 'root' })
export class CustomerListService implements CRUDViewService<CustomerModel> {

  displayMode = signal<'list' | 'cards'>('list');

  cache = inject(ViewDataCache);
  refresher = inject(Refresher);
  repo = inject(CustomerRepository);

  getListCache(key: string) {
    const cache = this.cache.get<CustomerViewModel>(viewID, key);
    if (!cache) {
      throw new Error(`No cache found for customer list view with key ${key}`);
    }
    return cache;
  }

  // registerView(viewKey: string) {
  //   this.getListCache(viewKey); // just to check if cache exists
  // }
  activateViewIdentity(key: string): Signal<CustomerViewModel> {
    const cacheSubscription = this.cache.subscribe<CustomerViewModel>(viewID, key, emptyPageViewModel<CustomerModel>());
    this.refresher.keepFresh(viewID, key, async () => {
      const refreshCache = this.cache.get<CustomerViewModel>(viewID, key);
      console.debug(`Refreshing customer list for view with key ${key}`);
      if (!refreshCache) {
        return;
      }
      const {queryParameters} = refreshCache();
      // console.log('Refreshing customer list with query parameters', queryParameters);
      const pagesToFetch = Math.max(Math.ceil(refreshCache().data.length / pageSize), 1); // minimum 1
      const pageConfig: PageQueryConfig = { ...queryParameters, lastKey: null, pageSize: pagesToFetch * pageSize }; // fetch all at once
      const page = await firstValueFrom(this.repo.getPage(pageConfig));// fetch all at once
      // console.log(`Fetched ${page.data.length} customers for view with key ${key} while refreshing`);
      console.log('Refresh customer page with parameters', pageConfig, 'and result', page);

      let newPagination = paginationData<CustomerModel>();
      newPagination = {...newPagination, data: [...page.data], lastKey: page.lastKey };
      newPagination.endReached = true; // ??? Still needed for new tables?
      this.cache.update(viewID, key, { ...refreshCache(), pagination: newPagination });
    });
    return cacheSubscription;
  }

  deactivateViewIdentity(key: string): void {
    this.cache.unsubscribe(viewID, key);
    this.refresher.discard(viewID, key);
  }

  changeQueryParameters(key: string, searchParameters: string[], sortColumn: string, sortDirection: 'asc' | 'desc') {
    const cache = this.cache.get<CustomerViewModel>(viewID, key);
    if (!cache) {
      return;
    }
    // console.log('Changing query parameters for customer view with key', key, searchParameters, sortColumn, sortDirection);
    this.cache.update(viewID, key, { ...cache(), queryParameters: { searchParameters, sortKey: sortColumn, sortDirection }, pagination: paginationData<CustomerModel>() });
    // console.log('Changed query parameters for customer view with key', key, searchParameters, sortColumn, sortDirection);
    this.getPage(key);
  }

  async getPage(key: string) {
    const cache = this.cache.get<CustomerViewModel>(viewID, key);
    if (!cache) {
      console.error('No cache found for customer list view ',viewID, ' with key ', key);
      return;
    }
    const {queryParameters} = cache();
    console.log('Getting customer page with parameters', queryParameters);
    const {pagination} = cache();
    const pageConfig: PageQueryConfig = { ...queryParameters, lastKey: pagination.lastKey, pageSize };
    const page = await firstValueFrom(this.repo.getPage(pageConfig));
    const newPagination = <PaginationData<CustomerModel>>{
      ...pagination,
      data: [...page.data],
      lastKey: page.lastKey,
      endReached: !page.lastKey
  };
    console.log(`Get page for customer view with key ${key}`, page);
    this.cache.update(viewID, key, { ...cache(), pagination: newPagination });
  }

  async createCustomer(key: string, customer: CustomerModel) {
    console.debug('Creating customer', customer);
    await firstValueFrom(this.repo.createItem(customer)).then(() => {
      this.refresher.refresh(viewID, key);
    }).catch(error => {
      console.error('Error creating customer', error);
    });
  }

  async deleteCustomer(key: string, customer: CustomerModel ) {
    await firstValueFrom(this.repo.deleteItem(customer.id)).then(() => {
      this.refresher.refresh(viewID, key);
    });
  }

  async deleteCustomerById(key: string, customerId: string ) {
    await firstValueFrom(this.repo.deleteItem(customerId)).then(() => {
      this.refresher.refresh(viewID, key);
    });
  }
}
